import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useNavigate, useLocation } from "react-router-dom";
import { Plus, Filter, AlertCircle, CheckCircle } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useSessionsData } from "../hooks/useSessionData";
import { useSessionsModal } from "../hooks/useSessionsModal";
import SessionsCalendar from "../components/sessions/SessionsCalendar";
import SessionListView from "../components/sessions/SessionListView";
import SessionModal from "../components/sessions/SessionModal";
import SessionsFilters from "../components/sessions/SessionsFilters";
import CalendarHeader from "../components/sessions/CalendarHeader";
import { usePermissions } from "../components/HasPermission";
import { useAuth } from "../App";
import { useApp } from "../contexts/AppContext";
import { sessionsAPI } from "../services/api";

// Calendar view types
const CALENDAR_VIEWS = {
  MONTHLY: "monthly",
  WEEKLY: "weekly",
  THREE_DAY: "three_day",
  DAILY: "daily",
};

// Display modes
const DISPLAY_MODES = {
  CALENDAR: "calendar",
  LIST: "list",
};

export const SessionsPage = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  // Organization comes from AppContext so this page reacts when the org
  // resolves AFTER mount. The old `useState(localStorage.getItem(...))`
  // froze whatever value existed at first render — on a fresh login
  // (logout wipes the stored id) that was always "", every fetch was
  // skipped, and the calendar stayed empty until a manual refresh.
  const { selectedOrganizationId, loading: organizationsLoading } = useApp();
  const organizationId = selectedOrganizationId || "";

  // Display mode state
  const [displayMode, setDisplayMode] = useState(DISPLAY_MODES.CALENDAR);

  const { hasPermission } = usePermissions();
  const { user } = useAuth();

  // Calendar state
  const [currentDate, setCurrentDate] = useState(moment());
  // On phones the 7-column week grid is unreadable — default to the 3-day view there.
  const [calendarView, setCalendarView] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 640
      ? CALENDAR_VIEWS.THREE_DAY
      : CALENDAR_VIEWS.WEEKLY,
  );
  const [showFilters, setShowFilters] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    selectedTrainers: location.state?.selectedTrainerId
      ? [location.state.selectedTrainerId]
      : [],
    studentSearch: "",
    sessionType: "all",
    sessionStatus: "all",
    selectedTrainerId: "all",
  });

  // Custom date range state for List view
  const [customDateRange, setCustomDateRange] = useState({
    startDate: moment().toDate(),
    endDate: moment().add(7, "days").toDate(),
  });

  // Messages state
  const [successMessage, setSuccessMessage] = useState(null);
  const [error, setError] = useState(null);
  const [updatingSessionIds, setUpdatingSessionIds] = useState([]);

  // Custom hooks for data management
  const {
    sessions,
    allSessions,
    trainers,
    students,
    loading,
    loadSessions,
    // loadTrainers and loadStudents removed to fix ESLint warnings
    error: dataError,
    updateLocalSession,
  } = useSessionsData(
    organizationId,
    currentDate,
    calendarView,
    filters,
    displayMode === "list" ? customDateRange : null,
  );

  // While the org list is still resolving there is no organizationId yet, so
  // session fetches haven't even started — show the skeleton instead of an
  // empty calendar during that window.
  const isLoading = loading || (organizationsLoading && !organizationId);

  const sessionTypes = React.useMemo(() => {
    const uniqueTypes = [...new Set(sessions.map((s) => s.packageName))];
    return uniqueTypes.filter(Boolean).sort(); // Remove null/undefined and sort
  }, [sessions]);

  const activeTrainers = React.useMemo(() => trainers.filter(t => t.status === 'active'), [trainers]);
  const activeStudents = React.useMemo(() => students.filter(s => s.studentStatus === 'active'), [students]);

  // One-time auto-apply: if the logged-in user's email matches any trainer in the left-side
  // filter (regardless of role — trainer / super_admin / manager), simulate a click on that
  // trainer. Uses `selectedTrainers` (client-side memo), so no refetch and no flicker.
  // Runs once per mount; respects any prior selection.
  const autoTrainerApplied = useRef(false);
  useEffect(() => {
    if (autoTrainerApplied.current) return;
    if (!user?.email) return;
    if (!activeTrainers || activeTrainers.length === 0) return;
    // Respect existing selection (user interaction, location.state, etc.)
    if (filters.selectedTrainers && filters.selectedTrainers.length > 0) return;

    const myTrainer = activeTrainers.find((t) => t.email === user.email);
    if (myTrainer) {
      setFilters((prev) => ({ ...prev, selectedTrainers: [myTrainer.id] }));
    }
    // Mark as done regardless of match — no need to re-check on future renders
    autoTrainerApplied.current = true;
  }, [user, activeTrainers, filters.selectedTrainers]);

  // Custom hook for modal management
  const {
    showModal,
    modalType,
    selectedEvent,
    formData,
    formErrors,
    submitting,
    formError,
    loadingSessionTypes,
    sessionTypes: modalSessionTypes,
    mainSessionTypes, // New
    subPackages, // New
    studentSubscriptions, // New
    loadingStudentSubscriptions, // New
    sessionTimes,
    openCreateModal,
    openEditModal,
    openGroupManageModal,
    closeModal,
    handleSubmit,
    handleDeleteSession,
    setFormData,
    setSessionTimes,
    addSessionTime,
    removeSessionTime,
    updateSessionTime,
    handleStudentsChange,
    // New for organization selection
    organizations,
    loadingOrganizations,
    isAllOrgsMode,
    isDemoSession,
    loadSessionTypes,
    modalStudents,
    modalTrainers,
  } = useSessionsModal(organizationId, activeStudents, activeTrainers, loadSessions);

  // Navigation functions
  const navigateDate = (direction) => {
    let newDate;
    switch (calendarView) {
      case CALENDAR_VIEWS.DAILY:
        newDate = moment(currentDate).add(direction, "day");
        break;
      case CALENDAR_VIEWS.THREE_DAY:
        newDate = moment(currentDate).add(direction * 3, "day");
        break;
      case CALENDAR_VIEWS.WEEKLY:
        newDate = moment(currentDate).add(direction, "week");
        break;
      case CALENDAR_VIEWS.MONTHLY:
      default:
        newDate = moment(currentDate).add(direction, "month");
        break;
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(moment());
  };

  // Handle session selection - always navigate to details page
  const handleSessionClick = (session) => {
    if (session.isGroupContainer) {
      // Navigate to details page using the first student's session ID
      // The detail page auto-discovers all sibling sessions in the same timeslot
      const firstStudentSessionId = session.students?.[0]?.session?.id || session.students?.[0]?.id;
      if (firstStudentSessionId) {
        navigate(`/sessions/${firstStudentSessionId}`);
      }
    } else {
      navigate(`/sessions/${session.id}`);
    }
  };

  const handleUpdateSessionStatus = async (sessionId, newStatus) => {
    try {
      setUpdatingSessionIds(prev => [...prev, sessionId]);
      await sessionsAPI.update(organizationId, sessionId, {
        status: newStatus,
      });
      // Instead of re-fetching everything (date-range), update locally
      updateLocalSession(sessionId, { status: newStatus });
    } catch (err) {
      console.error("Error updating session status:", err);
      setError("Seans durumu güncellenemedi.");
    } finally {
      setUpdatingSessionIds(prev => prev.filter(id => id !== sessionId));
    }
  };

  // Handle create session
  const handleCreateSession = (date) => {
    // Past date check: if date is before today (midnight)
    const isPast = moment(date).isBefore(moment().startOf('day'));
    if (isPast && !hasPermission('create:sessions:past')) {
      setError(t('sessionsPage.messages.pastDateError', "Geriye dönük seans oluşturma yetkiniz bulunmamaktadır."));
      return;
    }
    openCreateModal(date, filters.selectedTrainerId);
  };

  // Handle filter updates
  const updateFilters = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  // Clear location state on cleanup to avoid re-applying filter on navigation
  useEffect(() => {
    if (location.state?.selectedTrainerId) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.selectedTrainerId]);

  // Handle success message auto-clear
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Update error when data error changes
  useEffect(() => {
    if (dataError) {
      setError(dataError);
    }
  }, [dataError]);

  // Handle modal success
  const handleModalSuccess = (message) => {
    setSuccessMessage(message);
    closeModal();
  };

  const SuccessNotification = ({ message, onClose }) => {
    if (!message) return null;

    return (
      <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl shadow-lg backdrop-blur-sm animate-in slide-in-from-top-2">
        <div className="flex items-start">
          <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
          <div className="ml-3 flex-1">
            <p className="text-emerald-700 dark:text-emerald-300 font-medium">
              {message}
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-3 text-emerald-500 hover:text-emerald-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  const ErrorNotification = ({ message, onClose }) => {
    if (!message) return null;

    return (
      <div className="mb-6 p-4 bg-rose-500/10 border border-rose-500/20 rounded-2xl shadow-lg backdrop-blur-sm animate-in slide-in-from-top-2">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-rose-500 flex-shrink-0" />
          <div className="ml-3 flex-1">
            <p className="text-rose-700 dark:text-rose-300 font-medium">
              {message}
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-3 text-rose-500 hover:text-rose-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`flex h-full px-0 md:px-4 pb-0 md:pb-4 gap-0 md:gap-4 overflow-hidden`}
    >
      {/* Filters Sidebar - Desktop Only */}
      <div
        className={`
        hidden md:block 
        ${showFilters ? "md:w-72" : "md:w-0 overflow-hidden p-0"} 
        transition-all duration-500 ease-[cubic-bezier(0.25,1,0.5,1)]
      `}
      >
        <SessionsFilters
          showFilters={showFilters}
          onToggleFilters={() => setShowFilters(!showFilters)}
          filters={filters}
          onUpdateFilters={updateFilters}
          trainers={activeTrainers}
          sessionTypes={sessionTypes}
          loading={isLoading}
          sessions={allSessions}
        />
      </div>

      {/* Main Content */}
      <div
        className={`
        flex-1 flex flex-col overflow-hidden
        relative transition-all duration-500
      `}
      >
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 md:p-5 lg:p-6 custom-scrollbar">
          {/* Page Header Area */}
          <div className="flex items-center justify-between mb-3 md:mb-8 gap-2 md:gap-4">
            <div className="flex items-center gap-2 md:space-x-4 min-w-0">
              {/* Mobile Filter Button */}
              <button
                onClick={() => setShowMobileFilters(true)}
                className={`
                  md:hidden p-2.5 ${colors.bgHoverStrong} 
                  rounded-xl transition-all duration-300 shadow-sm flex-shrink-0
                `}
              >
                <Filter className={`w-4 h-4 ${colors.textSecondary}`} />
              </button>

              {!showFilters && (
                <button
                  onClick={() => setShowFilters(true)}
                  className={`
                    hidden md:block p-3 ${colors.bgHoverStrong} 
                    rounded-2xl transition-all duration-300 hover:scale-110 shadow-sm
                  `}
                >
                  <Filter className={`w-5 h-5 ${colors.textSecondary}`} />
                </button>
              )}
              <div className="flex items-center gap-2 min-w-0">
                <h1
                  className={`text-lg md:text-2xl font-black ${colors.textPrimary} tracking-tight truncate`}
                >
                  {t("sessionsPage.title")}
                </h1>
                <div className="px-3 py-1 bg-indigo-500/10 border border-indigo-500/20 rounded-full shadow-sm flex-shrink-0 animate-in fade-in zoom-in duration-700 delay-300">
                  <span className="text-[10px] md:text-xs text-indigo-400 font-black tracking-widest">
                    {sessions.length}
                  </span>
                </div>
              </div>
            </div>

            {/* Add Session Button - Desktop only */}
            {hasPermission("create:sessions") && (
              <button
                onClick={() => openCreateModal(null, filters.selectedTrainerId)}
                className="hidden md:flex items-center space-x-2 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-all duration-300 shadow-lg shadow-indigo-500/20 active:scale-95 group font-bold text-sm"
              >
                <Plus className="h-4 w-4 mr-2 transition-transform duration-300 group-hover:rotate-90" />
                <span>{t("sessionsPage.addSession")}</span>
              </button>
            )}
          </div>

          {/* Messages */}
          <SuccessNotification
            message={successMessage}
            onClose={() => setSuccessMessage(null)}
          />
          <ErrorNotification message={error} onClose={() => setError(null)} />

          {/* Calendar Header Control */}
          <div className="mb-3 md:mb-6">
            <CalendarHeader
              currentDate={currentDate}
              calendarView={calendarView}
              totalSessions={sessions.length}
              ignoreViewFilter={displayMode === "list"}
              onViewChange={setCalendarView}
              onNavigate={navigateDate}
              onGoToToday={goToToday}
              trainers={activeTrainers}
              selectedTrainerId={filters.selectedTrainerId}
              onTrainerChange={(trainerId) =>
                updateFilters({ selectedTrainerId: trainerId })
              }
              displayMode={displayMode}
              onDisplayModeChange={setDisplayMode}
              customDateRange={customDateRange}
              onCustomDateChange={setCustomDateRange}
            />
          </div>

          {/* Content Area */}
          <div className="relative animate-in fade-in zoom-in-95 duration-500">
            {displayMode === DISPLAY_MODES.CALENDAR ? (
              <SessionsCalendar
                view={calendarView}
                currentDate={currentDate}
                sessions={sessions}
                loading={isLoading}
                trainers={trainers}
                onSessionClick={handleSessionClick}
                onCreateSession={handleCreateSession}
              />
            ) : (
              <SessionListView
                sessions={sessions}
                loading={isLoading}
                trainers={trainers}
                onSessionClick={handleSessionClick}
                currentDate={currentDate}
                calendarView={calendarView}
                ignoreViewFilter={displayMode === "list"}
                onUpdateStatus={handleUpdateSessionStatus}
                updatingSessionIds={updatingSessionIds}
              />
            )}
          </div>
        </div>
      </div>

      {/* Mobile FAB - Add Session */}
      {hasPermission("create:sessions") && (
        <button
          onClick={() => openCreateModal(null, filters.selectedTrainerId)}
          className="
            md:hidden fixed bottom-6 right-5 z-50
            w-14 h-14 rounded-full
            bg-gradient-to-br from-violet-500 to-indigo-600
            text-white shadow-xl shadow-violet-500/40
            flex items-center justify-center
            transition-all duration-300 active:scale-90 hover:shadow-violet-500/60
          "
        >
          <Plus className="w-7 h-7" />
        </button>
      )}

      {/* Mobile Filters - Slide from left */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-[100] md:hidden">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowMobileFilters(false)}
          />
          <div
            className={`absolute top-0 left-0 bottom-0 w-[85%] max-w-[320px] ${colors.cardBg} flex flex-col shadow-2xl animate-in slide-in-from-left duration-300`}
          >
            <SessionsFilters
              showFilters={true}
              onToggleFilters={() => setShowMobileFilters(false)}
              filters={filters}
              onUpdateFilters={updateFilters}
              trainers={activeTrainers}
              sessionTypes={sessionTypes}
              loading={isLoading}
              sessions={allSessions}
            />
          </div>
        </div>
      )}

      {/* Session Modal */}
      <SessionModal
        isOpen={showModal}
        modalType={modalType}
        selectedEvent={selectedEvent}
        formData={formData}
        formErrors={formErrors}
        formError={formError}
        submitting={submitting}
        trainers={modalTrainers}
        students={modalStudents}
        sessionTypes={modalSessionTypes}
        mainSessionTypes={mainSessionTypes} // New
        subPackages={subPackages} // New
        studentSubscriptions={studentSubscriptions} // New
        loadingStudentSubscriptions={loadingStudentSubscriptions} // New
        loadingSessionTypes={loadingSessionTypes}
        sessionTimes={sessionTimes}
        organizationId={organizationId}
        onClose={closeModal}
        onSubmit={(data) => handleSubmit(data, handleModalSuccess)}
        onEditMode={() => openEditModal(selectedEvent)}
        onDelete={() => handleDeleteSession(handleModalSuccess)}
        onFormDataChange={setFormData}
        onSessionTimesChange={setSessionTimes}
        onAddSessionTime={addSessionTime}
        onRemoveSessionTime={removeSessionTime}
        onUpdateSessionTime={updateSessionTime}
        handleStudentsChange={handleStudentsChange}
        organizations={organizations}
        loadingOrganizations={loadingOrganizations}
        isAllOrgsMode={isAllOrgsMode}
        isDemoSession={isDemoSession}
        onLoadSessionTypes={loadSessionTypes}
      />
    </div>
  );
};
